import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Container,
    IconButton,
    Divider,
    Alert,
    Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Styled components for consistent styling
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    overflow: 'visible'
}));

const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
}));

const FormContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
}));

const DeviceManagement = () => {
    // State management
    const [screen, setScreen] = useState('read');
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [formValues, setFormValues] = useState({
        deviceID: '',
        deviceName: ''
    });
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch devices on component mount
    useEffect(() => {
        fetchDevices();
    }, []);

    // API functions
    const fetchDevices = async () => {
        try {
            const response = await fetch(`http://biometick.com/get-devices/${localStorage.getItem("userID")}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.length > 0) {
                setDevices(data);
            }
        } catch (error) {
            showNotification('Error fetching devices', 'error');
            console.error(error);
        }
    };

    const createDevice = async () => {
        try {
            const { deviceID, deviceName } = formValues;
            const myHeaders = new Headers();
            myHeaders.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
            myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

            const urlencoded = new URLSearchParams();
            urlencoded.append("userID", localStorage.getItem("userID"));
            urlencoded.append("deviceID", deviceID);
            urlencoded.append("deviceName", deviceName);

            const response = await fetch("http://biometick.com/post-device", {
                method: 'POST',
                headers: myHeaders,
                body: urlencoded
            });

            const result = await response.json();
            if (result._id) {
                showNotification('Device created successfully', 'success');
                fetchDevices();
                setScreen('read');
                resetForm();
            } else {
                showNotification('Error creating device', 'error');
            }
        } catch (error) {
            showNotification('Error creating device', 'error');
            console.error(error);
        }
    };

    const updateDevice = async () => {
        try {
            const response = await fetch(`http://biometick.com/modify-device/${selectedDevice._id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formValues)
            });

            const result = await response.json();
            if (result._id) {
                showNotification('Device updated successfully', 'success');
                fetchDevices();
                setScreen('read');
                resetForm();
            } else {
                showNotification('Error updating device', 'error');
            }
        } catch (error) {
            showNotification('Error updating device', 'error');
            console.error(error);
        }
    };

    const deleteDevice = async (id) => {
        try {
            const response = await fetch(`http://biometick.com/delete-device/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                showNotification('Device deleted successfully', 'success');
                setDevices(devices.filter(device => device._id !== id));
            } else {
                showNotification('Error deleting device', 'error');
            }
        } catch (error) {
            showNotification('Error deleting device', 'error');
            console.error(error);
        }
    };

    // UI Helper functions
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormValues(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditClick = (device) => {
        setSelectedDevice(device);
        setFormValues({
            deviceID: device.deviceID,
            deviceName: device.deviceName
        });
        setScreen('modify');
    };

    const resetForm = () => {
        setFormValues({
            deviceID: '',
            deviceName: ''
        });
        setSelectedDevice(null);
    };

    const showNotification = (message, severity) => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({
            ...prev,
            open: false
        }));
    };

    const toggleScreen = () => {
        if (screen === 'read') {
            setScreen('write');
            resetForm();
        } else {
            setScreen('read');
            resetForm();
        }
    };

    // Component rendering based on current screen
    const renderActionButton = () => {
        if (screen === 'read') {
            return (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={toggleScreen}
                    startIcon={<AddIcon />}
                    sx={{ borderRadius: 2 }}
                >
                    Add Device
                </Button>
            );
        } else {
            return (
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={toggleScreen}
                    startIcon={<ArrowBackIcon />}
                    sx={{ borderRadius: 2 }}
                >
                    Back to List
                </Button>
            );
        }
    };

    const renderDeviceTable = () => (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Table aria-label="device table">
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.light' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Device Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Device ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {devices.length > 0 ? (
                        devices.map((device) => (
                            <TableRow key={device._id} hover>
                                <TableCell>{device.deviceName}</TableCell>
                                <TableCell>{device.deviceID}</TableCell>
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        aria-label="edit"
                                        onClick={() => handleEditClick(device)}
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        aria-label="delete"
                                        onClick={() => deleteDevice(device._id)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={3} align="center">No devices found. Add a new device to get started.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderDeviceForm = (isEdit = false) => (
        <StyledCard>
            <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                    {isEdit ? 'Edit Device' : 'Create New Device'}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <FormContainer>
                    <TextField
                        label="Device Name"
                        name="deviceName"
                        variant="outlined"
                        fullWidth
                        value={formValues.deviceName}
                        onChange={handleFormChange}
                        required
                    />
                    <TextField
                        label="Device ID"
                        name="deviceID"
                        variant="outlined"
                        fullWidth
                        value={formValues.deviceID}
                        onChange={handleFormChange}
                        required
                    />
                </FormContainer>

                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={isEdit ? updateDevice : createDevice}
                    sx={{ height: '50px', borderRadius: 2 }}
                >
                    {isEdit ? 'Save Changes' : 'Create Device'}
                </Button>
            </CardContent>
        </StyledCard>
    );

    // Main component render
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <StyledCard>
                <CardContent>
                    <HeaderBox>
                        <Typography variant="h5" component="h1" color="primary" fontWeight="500">
                            Device Management
                        </Typography>
                        {renderActionButton()}
                    </HeaderBox>

                    {screen === 'read' && renderDeviceTable()}
                    {screen === 'write' && renderDeviceForm(false)}
                    {screen === 'modify' && renderDeviceForm(true)}
                </CardContent>
            </StyledCard>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default DeviceManagement;